import { AnyScale, ScaleValue, TicksSpec } from '@dfnivo/scales'
import { AxisLegendPosition, ValueFormatter } from '../types'
import { CompleteTheme, degreesToRadians } from '@dfnivo/core'
import { computeCartesianTicks } from '../compute'
import {
    CanvasLine,
    CanvasRenderable,
    CanvasRenderableProps,
    CanvasText,
    CanvasTransformItems,
} from '@dfnivo/canvas'

export type GetCanvasAxisProps<Value extends ScaleValue> = {
    axis: 'x' | 'y'
    scale: AnyScale
    x?: number
    y?: number
    length: number
    ticksPosition: 'before' | 'after'
    tickValues?: TicksSpec<Value>
    tickSize?: number
    tickPadding?: number
    tickRotation?: number
    format?: string | ValueFormatter<Value>
    legend?: string
    legendPosition?: AxisLegendPosition
    legendOffset?: number
    theme: CompleteTheme
}

export const getCanvasAxis = <Value extends ScaleValue>({
    axis,
    scale,
    x = 0,
    y = 0,
    length,

    ticksPosition,
    tickValues,
    tickSize = 5,
    tickPadding = 5,
    tickRotation = 0,
    format: _format,

    legend,
    legendPosition = 'end',
    legendOffset = 0,

    theme,
}: GetCanvasAxisProps<Value>) => {
    const { ticks, textAlign, textBaseline } = computeCartesianTicks({
        axis,
        scale,
        ticksPosition,
        tickValues,
        tickSize,
        tickPadding,
        tickRotation,
        engine: 'canvas',
        length,
    })
    const transform: CanvasTransformItems[] = [['translate', [x, y]]]
    const renderables: CanvasRenderable[] = []
    const commonProps: CanvasRenderableProps = {
        transform,
        textAlign,
        textBaseline,
        font: `${theme.axis.ticks.text.fontWeight ? `${theme.axis.ticks.text.fontWeight} ` : ''}${
            theme.axis.ticks.text.fontSize
        }px ${theme.axis.ticks.text.fontFamily}`,
    }

    if (parseFloat(String(theme.axis.domain.line.strokeWidth ?? 0)) > 0) {
        renderables.push(
            new CanvasLine(
                {
                    ...commonProps,
                    strokeStyle: theme.axis.domain.line.stroke,
                    lineCap: 'square',
                    lineWidth: Number(theme.axis.domain.line.strokeWidth),
                    x1: 0,
                    y1: 0,
                    x2: axis === 'x' ? length : 0,
                    y2: axis === 'x' ? 0 : length,
                },
                undefined
            )
        )
    }

    const format = typeof _format === 'function' ? _format : (value: unknown) => `${value}`

    ticks.forEach(tick => {
        if (parseFloat(String(theme.axis.ticks.line.strokeWidth ?? 0)) > 0) {
            renderables.push(
                new CanvasLine(
                    {
                        ...commonProps,
                        lineCap: 'square',
                        strokeStyle: theme.axis.ticks.line.stroke,
                        lineWidth: Number(theme.axis.ticks.line.strokeWidth),
                        x1: tick.x,
                        y1: tick.y,
                        x2: tick.x + tick.lineX,
                        y2: tick.y + tick.lineY,
                    },
                    undefined
                )
            )
        }

        const value = format(tick.value)
        renderables.push(
            new CanvasText(
                {
                    ...commonProps,
                    fillStyle: theme.axis.ticks.text.fill,
                    text: String(value),
                    x: 0, //tick.x,
                    y: 0, //tick.y,
                    transform: [
                        ...transform,
                        ['translate', [tick.textX + tick.x, tick.textY + tick.y]],
                        ['rotate', [degreesToRadians(tickRotation)]],
                    ],
                },
                undefined
            )
        )
    })

    if (legend !== undefined) {
        let legendX = 0
        let legendY = 0
        let legendRotation = 0
        let textAlign: CanvasTextAlign = 'center'

        if (axis === 'y') {
            legendRotation = -90
            legendX = legendOffset
            if (legendPosition === 'start') {
                textAlign = 'start'
                legendY = length
            } else if (legendPosition === 'middle') {
                textAlign = 'center'
                legendY = length / 2
            } else if (legendPosition === 'end') {
                textAlign = 'end'
            }
        } else {
            legendY = legendOffset
            if (legendPosition === 'start') {
                textAlign = 'start'
            } else if (legendPosition === 'middle') {
                textAlign = 'center'
                legendX = length / 2
            } else if (legendPosition === 'end') {
                textAlign = 'end'
                legendX = length
            }
        }

        renderables.push(
            new CanvasText(
                {
                    ...commonProps,
                    x: 0,
                    y: 0,
                    text: legend,
                    textAlign,
                    textBaseline: 'middle',
                    transform: [
                        ...transform,
                        ['translate', [legendX, legendY]],
                        ['rotate', [degreesToRadians(legendRotation)]],
                    ],
                    fillStyle: theme.axis.legend.text.fill,
                    font: `${
                        theme.axis.legend.text.fontWeight
                            ? `${theme.axis.legend.text.fontWeight} `
                            : ''
                    }${theme.axis.legend.text.fontSize}px ${theme.axis.legend.text.fontFamily}`,
                },
                undefined
            )
        )
    }
    return renderables
}

export const renderAxisToCanvas = <Value extends ScaleValue>(
    ctx: CanvasRenderingContext2D,
    props: GetCanvasAxisProps<Value>
) => {
    getCanvasAxis(props).forEach(item => item.render(ctx))
}
